/**
 * product-tabs-card.js
 * Horizon theme – Product Tabs Card interaction layer.
 *
 * Fixes applied:
 *  1. Fly image is larger (sourceEl ref kept live; CSS bumped to 88 px).
 *  2. Carousel / scroll miss fixed: sourcePoint is re-read from the live
 *     element at the moment flyToCart() launches, not from a stale pointerdown
 *     snapshot that may be wrong after async fetch + page scroll.
 *  3. Cart-bubble animation sync fixed: refreshCartCount() no longer writes
 *     directly to the bubble DOM. It only dispatches cart:update so CartIcon
 *     drives the bounce animation exclusively, and only after flyToCart resolves.
 *  4. Source point clamped to visible viewport: when the card is at the bottom
 *     of a product page with a long description, the image centre could be
 *     partially or fully above/below the fold. getLivePoint now clamps the
 *     returned point to the *intersection* of the element rect and the viewport
 *     so the projectile always appears to start from a visible location.
 *  5. Destination point: isUsableElement previously walked every ancestor and
 *     rejected an element the moment any ancestor had opacity:0 during a scroll-
 *     triggered fade or page-transition. The cart icon itself is always opaque
 *     so we now only check the element's own computed style, not its ancestors.
 *     This prevents getFallbackCartPoint() from being used when the real cart
 *     icon is perfectly visible, fixing the "lands slightly left of cart" bug.
 *  6. Optimistic animation: flyToCart now launches immediately on click,
 *     in parallel with the fetch, instead of waiting for the network round-trip
 *     to complete. The cart:update dispatch still happens only after BOTH the
 *     fetch AND the fly animation finish, preserving the correct bounce timing.
 *     If the fetch fails the animation has already played — this is acceptable
 *     UX (identical to how native Shopify themes handle optimistic add-to-cart).
 *  7. Animation source point fix: createFlySnapshot now prefers the actual
 *     <img> element (.ptc__img--primary) over the .ptc__media container, since
 *     the container may have zero dimensions when using aspect-ratio or flex
 *     layouts. A find() walk tries imgEl → mediaEl → card and uses the first
 *     with a non-zero painted rect. WeakMap lookup now uses .has() to correctly
 *     distinguish "no pointerdown fired" from "pointerdown fired but null
 *     snapshot", preventing a stale scrolled-rect re-read at click time that
 *     caused the animation to appear to start from the add-to-cart button.
 *  8. Quantity desync fix: the card quantity was updated optimistically and the
 *     cart was hit with one concurrent absolute-quantity request per tap. Rapid
 *     taps raced and settled out of order, so the real cart (and the bubble)
 *     drifted from the displayed value; navigating mid-flight aborted requests
 *     and the bfcache then restored a frozen, wrong ptc__qty-val. The cart logic
 *     now mirrors dynamic-atc.js: a single serialized, coalesced /cart/update
 *     request per card (data-ptc-desired is the source of truth) so the cart
 *     lands on the shown value, and reconcileCardsWithCart() re-reads the live
 *     cart on every sync AND on `pageshow` (initial load + bfcache) to rewrite
 *     each card's quantity and re-dispatch cart:update so the bubble stays in
 *     sync. This supersedes the refreshCartCount() mechanism from fix #3/#6.
 *  9. Follow-ups to #8: (a) bfcache restores now dispatch cart:update — CartIcon's
 *     ensureCartBubbleIsCorrect ignores sessionStorage older than 10s, so the
 *     frozen bubble was left wrong after spending time on another page;
 *     (b) syncCart awaits the in-flight cart snapshot and delta-corrects an
 *     open-from-closed click (data-ptc-open-base), so an absolute /cart/update
 *     can never clobber an already-in-cart quantity when "+" is tapped before
 *     the initial /cart.js fetch lands; (c) cards listen for cart:update from
 *     other components (cart drawer, product form, dynamic-atc) and reconcile,
 *     so steppers can't go stale when the cart is changed elsewhere.
 * 10. Cart-bubble sync across navigation: (a) cart/update writes use keepalive so
 *     a fast "add a few, then open a product page" no longer aborts the writes —
 *     the cart reaches the tapped total; (b) the destination page corrects its
 *     server-rendered bubble on load via reconcile({dispatchIfStale}) — dispatch
 *     only when the live /cart.js count differs from the rendered bubble, plus a
 *     single delayed re-pull to catch writes that land just after load; (c) the
 *     /cart.js read is cache:'no-store' so a cached response can't reintroduce a
 *     stale count. Also fixed an ordering bug where the post-sync reconcile ran
 *     before the pending-tap re-flush, silently discarding a tap made during the
 *     fly animation — the re-flush now runs first.
 */

(function () {
  if (window.ptcInitialized) return;
  window.ptcInitialized = true;

  /**
   * Snapshot stores a pre-captured viewport point (taken at pointerdown) and
   * the image URL for the fly thumbnail.
   *
   * Why a pre-captured point instead of a live element reference:
   * - At pointerdown the user's finger/cursor is ON the card, so it is
   *   guaranteed to be in the viewport and have a valid, painted rect.
   * - Carousel slides inside <slideshow-slides> can have transforms or
   *   overflow:hidden that make getBoundingClientRect() return wrong values
   *   for off-screen slides. Reading at pointerdown avoids that entirely.
   * - Lazy images (<img loading="lazy">) have zero rect until painted.
   *   Using the .ptc__media container at pointerdown (when the card is
   *   visible) gives a correct rect before any lazy-load issues arise.
   * - The previous "live reference, read at launch time" approach broke
   *   with optimistic animation: the animation now fires before the fetch,
   *   so there is no longer any scroll between pointerdown and launch —
   *   a snapshot is perfectly accurate.
   *
   * @typedef {{ sourcePoint: {x: number, y: number}, imageUrl: string }} FlySnapshot
   */

  /** @type {WeakMap<Element, FlySnapshot | null>} */
  const flySnapshots = new WeakMap();
  const FLY_ELEMENT_WAIT_TIMEOUT = 700;
  const FLY_ANIMATION_TIMEOUT = 3200;

  // Read `Theme` bare, guarded with typeof: scripts.liquid declares it as a top-level
  // `const` in a classic script, so it lives in the global lexical scope and never becomes
  // a window property — window.Theme alone always yielded {} and silently fell back to the
  // root-relative literals below. Mirrors getRoutes() in product-card-add.js.
  const routes = typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : window.Theme?.routes || {};
  // /cart/update sets an ABSOLUTE quantity by variant id (and adds the line when
  // it isn't in the cart yet), so a single endpoint handles add / change / remove.
  const cartUpdateUrl = toJsonRoute(routes.cart_update_url || '/cart/update');
  const cartUrl = toJsonRoute(routes.cart_url || '/cart');

  // Last-known cart quantity per variant id, refreshed on every reconcile. Used
  // when opening a closed card so the first "+" continues from the real cart
  // quantity instead of clobbering an already-in-cart item down to 1.
  const cartQtyCache = new Map();

  document.addEventListener(
    'pointerdown',
    function (e) {
      const addBtn = closestFromEvent(e, '[data-ptc-add-btn]');
      if (!addBtn) return;

      const card = addBtn.closest('[data-ptc-card]');
      if (!card) return;

      // Capture the snapshot NOW — at pointerdown the card is guaranteed to be
      // in the viewport, fully painted, with a valid rect.
      flySnapshots.set(addBtn, createFlySnapshot(card));
    },
    { passive: true, capture: true }
  );

  document.addEventListener('click', function (e) {
    const addBtn = closestFromEvent(e, '[data-ptc-add-btn]');

    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();

      const card = addBtn.closest('[data-ptc-card]');
      if (!card) return;

      // FIX #7 — Use .has() to distinguish between:
      //   a) pointerdown never fired → no key → fall back to fresh snapshot
      //   b) pointerdown fired but card wasn't painted → key present, value null
      // Previously `flySnapshots.get(addBtn) || createFlySnapshot(card)` treated
      // both cases identically and re-read a potentially scrolled rect at click
      // time, causing the animation to start from the button area instead of the
      // product image.
      const flySnapshot = flySnapshots.has(addBtn)
        ? flySnapshots.get(addBtn)
        : createFlySnapshot(card);

      flySnapshots.delete(addBtn);

      const wasOpen = card.classList.contains('ptc--qty-open');
      let desired = currentDesired(card);

      if (!wasOpen) {
        card.classList.add('ptc--qty-open');
        // Continue from the known cart quantity so opening a card whose item is
        // already in the cart (e.g. added on the product page) never resets it.
        const variantId = card.querySelector('[data-ptc-form] [name="id"]')?.value;
        const known = (variantId && cartQtyCache.get(String(variantId))) || 0;
        // Remember the base we assumed: if the cart snapshot is still loading,
        // the cache may be empty/stale, and syncCart corrects the target by the
        // difference once the snapshot lands (see the openBase block there).
        card.dataset.ptcOpenBase = String(known);
        desired = Math.min(known + 1, 99);
      } else if (desired < 99) {
        desired += 1;
      } else {
        return;
      }

      setCardQty(card, desired);
      launchFly(card, flySnapshot);
      syncCart(card);

      return;
    }

    const minusBtn = closestFromEvent(e, '[data-qty-minus]');

    if (minusBtn) {
      e.preventDefault();
      e.stopPropagation();

      const card = minusBtn.closest('[data-ptc-card]');
      if (!card) return;

      const desired = currentDesired(card);

      if (desired > 1) {
        setCardQty(card, desired - 1);
      } else {
        // Stepping below 1 removes the line and collapses the card to its closed
        // "+" state. The visible value resets to 1 for the closed UI, but the
        // desired quantity sent to the cart is 0.
        card.classList.remove('ptc--qty-open');
        card.dataset.ptcDesired = '0';
        setDisplay(card, 1);
      }

      syncCart(card);
    }
  });

  // ---------------------------------------------------------------------------
  // Quantity helpers + serialized cart sync
  // ---------------------------------------------------------------------------

  /**
   * The desired absolute quantity for a card is the single source of truth,
   * stored on data-ptc-desired. Falls back to the displayed value (e.g. right
   * after a server-rendered load or a reconcile) when not yet set.
   *
   * @param {Element} card
   * @returns {number}
   */
  function currentDesired(card) {
    const display = card.querySelector('[data-qty-value]');
    const raw = card.dataset.ptcDesired ?? display?.textContent ?? '0';
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? 0 : n;
  }

  /** Sets the desired quantity AND the visible stepper value. */
  function setCardQty(card, qty) {
    card.dataset.ptcDesired = String(qty);
    setDisplay(card, qty);
  }

  /** Updates only the visible stepper value (display span + hidden input). */
  function setDisplay(card, qty) {
    const display = card.querySelector('[data-qty-value]');
    const input = card.querySelector('[data-qty-input]');
    if (display) display.textContent = qty;
    if (input) input.value = qty;
  }

  /**
   * Launches the optimistic fly-to-cart animation for an increment and stores
   * its promise on the card so the sync can hold the cart-bubble bounce until
   * the projectile lands (preserving the original FIX #3 / #6 timing).
   */
  function launchFly(card, flySnapshot) {
    showAddedState(card);
    window.__ptcFlyToCartLock = true;
    card._ptcFlyPromise = flyToCart(flySnapshot).finally(() => {
      window.__ptcFlyToCartLock = false;
    });
  }

  /**
   * Serialized, coalesced cart sync for a single card.
   *
   * Rapid +/- taps must NOT fire concurrent absolute-quantity requests: with
   * absolute (/cart/update) semantics those can settle out of order and leave
   * the real cart — and the cart bubble — disagreeing with the displayed
   * quantity, which in turn makes the minus button operate on a wrong number.
   * Mirroring dynamic-atc.js, only one request is in flight per card at a time;
   * the loop always re-reads the latest desired quantity, so a burst of taps
   * collapses into the minimum number of ordered requests and the cart ends on
   * the value actually shown.
   *
   * @param {Element} card
   */
  async function syncCart(card) {
    if (card._ptcSyncing) return; // a loop is already draining this card
    card._ptcSyncing = true;
    card.dataset.ptcPending = '1'; // tell reconcileCardsWithCart to skip us

    const variantInput = card.querySelector('[data-ptc-form] [name="id"]');
    const qtyWrap = card.querySelector('[data-ptc-qty-wrap]');

    if (!variantInput?.value) {
      card._ptcSyncing = false;
      delete card.dataset.ptcPending;
      return;
    }

    // Never race a stale view of the cart: wait for the in-flight snapshot
    // (initial load / bfcache restore) before sending absolute quantities.
    // Resolved long ago in the common case, so this normally adds no delay.
    // (Snapshot failures are non-fatal — we proceed with what we have rather
    // than leaving the card stuck in its pending state.)
    await Promise.resolve(cartSnapshot).catch(() => {});

    // If this sync started by opening a closed card, the assumed base quantity
    // may have been read from a not-yet-populated cache. Now that the snapshot
    // is in, shift the target by the difference so an absolute update can never
    // clobber an already-in-cart quantity down to 1.
    const openBase = card.dataset.ptcOpenBase;
    if (openBase !== undefined) {
      delete card.dataset.ptcOpenBase;
      const realBase = cartQtyCache.get(String(variantInput.value)) || 0;
      const diff = realBase - (parseInt(openBase, 10) || 0);
      if (diff !== 0) {
        setCardQty(card, clamp(currentDesired(card) + diff, 1, 99));
      }
    }

    let lastSent = null;
    let ok = false;

    try {
      // Drain to the latest desired quantity, one ordered request at a time.
      while (true) {
        const desired = currentDesired(card);
        if (desired === lastSent) break;
        lastSent = desired;

        const r = await fetch(
          cartUpdateUrl,
          cartFetchConfig({ updates: { [variantInput.value]: desired } })
        );

        if (!r.ok) throw new Error('cart/update failed');
      }

      // Hold the bubble bounce until the most recent fly lands.
      if (card._ptcFlyPromise) {
        try {
          await card._ptcFlyPromise;
        } catch {
          /* ignore animation errors */
        }
      }

      ok = true;
    } catch {
      ok = false;
    } finally {
      card._ptcSyncing = false;
      delete card.dataset.ptcPending;
    }

    showFeedback(qtyWrap, ok);

    // A tap that landed after the drain loop broke (e.g. during the fly-to-cart
    // animation, which holds the lock for the full animation) left an unsent
    // quantity. Flush it BEFORE reconciling: reconcile rewrites data-ptc-desired
    // from the now-stale cart, which would silently discard that tap and snap the
    // displayed quantity back. The re-issued sync runs its own reconcile when it
    // settles, so the bubble still ends up correct — just one cycle later.
    if (currentDesired(card) !== lastSent) {
      syncCart(card);
      return;
    }

    // No pending tap: pull the authoritative cart so the bubble + drawer sync,
    // sibling cards for this variant update, and — now that the pending flag is
    // cleared — this card resets to reality if the request failed.
    await reconcileCardsWithCart({ dispatch: true });
  }

  /**
   * Reads the cart count currently shown in the header bubble, or null if no
   * bubble is on the page. Empty/hidden bubble (empty cart) reads as 0.
   *
   * @returns {number | null}
   */
  function readRenderedCartCount() {
    const el = document.querySelector('cart-icon .cart-bubble__text-count, .cart-bubble__text-count');
    if (!el) return null;
    const n = parseInt(el.textContent ?? '', 10);
    return Number.isNaN(n) ? 0 : n;
  }

  /**
   * Dispatches a cart:update so CartIcon (and the cart drawer) re-render the
   * bubble from the authoritative item count.
   *
   * @param {Object} cart
   */
  function dispatchCartUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'product-tabs-card',
          data: {
            itemCount: cart.item_count,
            source: 'product-tabs-card',
          },
        },
      })
    );
  }

  /**
   * Reconciles every product-tabs-card on the page with the authoritative cart.
   *
   * The card quantity is only ever updated optimistically on click. That value
   * can drift from the real cart when in-flight cart requests are aborted by a
   * navigation — e.g. tapping "+" several times then opening a product page
   * before the requests finish. The back/forward cache later restores the stale
   * DOM, so ptc__qty-val ends up frozen at a number that was never actually
   * added (showing 8 when the cart really holds 3).
   *
   * Running on `pageshow` (which fires on the initial load AND on bfcache
   * restore) pulls the real per-variant quantities from /cart.js and rewrites
   * each card to match, so the displayed count always reflects the cart.
   *
   * Cards with an in-flight request (data-ptc-pending) are skipped so we never
   * clobber a fresh optimistic value mid-update.
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.dispatch] - Always broadcast cart:update afterwards.
   * @param {boolean} [opts.dispatchIfStale] - Broadcast only when the live cart
   *   count differs from the header bubble. Used on initial load: a page reached
   *   by navigating away mid-write is server-rendered with a stale count, so we
   *   correct the bubble — without waking every cart listener on normal loads.
   */
  async function reconcileCardsWithCart({ dispatch = false, dispatchIfStale = false } = {}) {
    let cart;
    try {
      // no-store: never let a cached /cart.js response feed a stale count back
      // into the bubble.
      const r = await fetch(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!r.ok) return;
      cart = await r.json();
    } catch {
      return;
    }

    // variantId (string) -> total quantity across all matching line items.
    // Refresh the shared cache so the open-from-closed path has fresh numbers.
    const qtyByVariant = cartQtyCache;
    qtyByVariant.clear();
    for (const item of cart.items || []) {
      const key = String(item.variant_id ?? item.id);
      qtyByVariant.set(key, (qtyByVariant.get(key) || 0) + item.quantity);
    }

    document.querySelectorAll('[data-ptc-card]').forEach((card) => {
      if (card.dataset.ptcPending) return;

      const variantInput = card.querySelector('[data-ptc-form] [name="id"]');
      const display = card.querySelector('[data-qty-value]');
      if (!variantInput || !display) return;

      const qty = qtyByVariant.get(String(variantInput.value)) || 0;

      if (qty > 0) {
        card.classList.add('ptc--qty-open');
        setCardQty(card, qty);
      } else {
        card.classList.remove('ptc--qty-open');
        card.dataset.ptcDesired = '0';
        setDisplay(card, 1);
      }
    });

    // Broadcast after a real cart mutation (syncCart), or on load/bfcache when
    // the header bubble is out of step with the live cart — which happens when
    // the page was reached by navigating away before the writes finished, so it
    // was server-rendered with a stale count. The dispatchIfStale guard keeps
    // normal loads (count already correct) from waking every cart:update listener.
    let shouldDispatch = dispatch;
    if (!shouldDispatch && dispatchIfStale) {
      const rendered = readRenderedCartCount();
      shouldDispatch = rendered !== null && rendered !== cart.item_count;
    }

    if (shouldDispatch) dispatchCartUpdate(cart);
  }

  // Sync as soon as this (defer'd) script runs — the cards already exist, so we
  // catch up to the real cart well before the load-triggered pageshow fires.
  // syncCart awaits this promise so absolute updates never race the snapshot.
  // dispatchIfStale corrects the header bubble when this page was server-rendered
  // with a count that the keepalive writes from the previous page have since
  // moved past (the "added items, then opened this page" case).
  let cartSnapshot = reconcileCardsWithCart({ dispatchIfStale: true });

  // Those keepalive writes can also land just AFTER this reconcile fires (fast
  // page, slow write). One delayed re-pull settles the bubble on the final count
  // without polling. No-ops when nothing changed (dispatchIfStale stays quiet).
  setTimeout(() => {
    cartSnapshot = reconcileCardsWithCart({ dispatchIfStale: true });
  }, 1500);

  // Re-sync on every back/forward-cache restore, where the DOM is frozen with a
  // possibly stale optimistic quantity. Dispatch here too: CartIcon's own
  // bfcache correction (ensureCartBubbleIsCorrect) ignores sessionStorage older
  // than 10s, so without a dispatch the frozen bubble stays wrong whenever the
  // user spent more than a few seconds on the other page.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) cartSnapshot = reconcileCardsWithCart({ dispatch: true });
  });

  // When anything else mutates the cart (cart drawer, product form, dynamic-atc
  // on a product page with recommendation cards), re-pull the authoritative cart
  // so the steppers don't go stale and the next +/- operates on the right base.
  // Our own dispatches are skipped, and reconcile never re-dispatches here, so
  // this cannot loop. Debounced to collapse bursts from rapid drawer steppers.
  let foreignCartUpdateTimer = null;
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'product-tabs-card') return;

    clearTimeout(foreignCartUpdateTimer);
    foreignCartUpdateTimer = setTimeout(() => {
      cartSnapshot = reconcileCardsWithCart();
    }, 100);
  });

  function showFeedback(el, success) {
    if (!el) return;

    el.style.background = success ? '#dcfce7' : '#fee2e2';

    setTimeout(() => {
      el.style.background = '';
    }, 300);
  }

  function showAddedState(card) {
    card.classList.add('ptc--added');

    setTimeout(() => {
      card.classList.remove('ptc--added');
    }, 1500);
  }

  /**
   * Captures a fly snapshot at the moment of calling (intended to be called
   * from the pointerdown handler, when the card is in-viewport and painted).
   *
   * FIX #7 — SOURCE ELEMENT PRIORITY:
   *   Tries .ptc__img--primary (the actual <img>) → .ptc__media (container) →
   *   card root, using the first that has a non-zero painted rect.
   *
   *   Previously only .ptc__media was tried. When that container uses
   *   aspect-ratio or is a flex child, it can have zero dimensions before the
   *   browser has committed a layout pass, causing createFlySnapshot to return
   *   null. The click handler then called createFlySnapshot again at click time
   *   with a scrolled (wrong) rect, making the animation appear to start from
   *   the add-to-cart button rather than the product image.
   *
   * IMAGE URL — uses data-product-image (the tiny CDN thumbnail on the card
   *   root element, e.g. ?width=100) rather than the lazy <img> src:
   *   The lazy src may not be loaded yet, causing a black square. The
   *   data-product-image URL is tiny, loads near-instantly, and is always
   *   the correct product image.
   *
   * @param {Element} card
   * @returns {FlySnapshot | null}
   */
  function createFlySnapshot(card) {
    // FIX #7 — Walk from most-specific to least-specific and use the first
    // element that has a non-zero painted bounding rect.
    const imgEl   = card.querySelector('.ptc__img--primary, .ptc__img');
    const mediaEl = card.querySelector('.ptc__media');

    const sourceEl = [imgEl, mediaEl, card].find((el) => {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });

    if (!sourceEl) return null;

    const rect = sourceEl.getBoundingClientRect();

    // Centre of the visible slice of the source element.
    const visLeft   = Math.max(rect.left,   0);
    const visTop    = Math.max(rect.top,    0);
    const visRight  = Math.min(rect.right,  window.innerWidth);
    const visBottom = Math.min(rect.bottom, window.innerHeight);

    const sourcePoint = (visRight > visLeft && visBottom > visTop)
      ? { x: (visLeft + visRight) / 2, y: (visTop + visBottom) / 2 }
      : { x: clamp(rect.left + rect.width  / 2, 24, window.innerWidth  - 24),
          y: clamp(rect.top  + rect.height / 2, 24, window.innerHeight - 24) };

    // Prefer data-product-image (always a loaded, small CDN URL) over the
    // lazy <img> src which may not be decoded yet and would show as black.
    const imageUrl =
      card.dataset.productImage ||
      imgEl?.currentSrc ||
      imgEl?.src;

    if (!imageUrl) return null;

    return { sourcePoint, imageUrl };
  }

  /**
   * Reads the live viewport rect of the source element at launch time and
   * drives the fly-to-cart element. Resolves when the animation finishes.
   *
   * @param {FlySnapshot | null} snapshot
   * @returns {Promise<void>}
   */
  function flyToCart(snapshot) {
    return new Promise((resolve) => {
      if (!snapshot?.sourcePoint || !snapshot?.imageUrl) {
        resolve();
        return;
      }

      let resolved = false;
      let waitTimer = null;
      let animationTimer = null;

      const finish = () => {
        if (resolved) return;
        resolved = true;
        clearTimeout(waitTimer);
        clearTimeout(animationTimer);
        resolve();
      };

      waitTimer = setTimeout(finish, FLY_ELEMENT_WAIT_TIMEOUT);

      // Use the pre-captured sourcePoint from pointerdown — the card was
      // guaranteed visible at that moment. Since the animation now fires
      // immediately (optimistic, no fetch delay), the point is still accurate.
      const sourcePoint = snapshot.sourcePoint;
      const destinationPoint = getCartDestinationPoint();

      if (!sourcePoint || !destinationPoint) {
        finish();
        return;
      }

      const launch = () => {
        if (resolved || !customElements.get('fly-to-cart')) {
          finish();
          return;
        }

        clearTimeout(waitTimer);

        const flyEl = document.createElement('fly-to-cart');

        flyEl.classList.add('fly-to-cart--quick', 'ptc-fly-to-cart');

        flyEl.style.setProperty(
          'background-image',
          `url("${escapeCssUrl(snapshot.imageUrl)}")`
        );

        flyEl.useSourceSize = false;
        flyEl.sourcePoint = sourcePoint;
        flyEl.destinationPoint = destinationPoint;

        flyEl.addEventListener('fly-to-cart:done', finish, { once: true });

        document.body.appendChild(flyEl);

        // Safety timeout — never block the cart update forever.
        animationTimer = setTimeout(finish, FLY_ANIMATION_TIMEOUT);
      };

      if (customElements.get('fly-to-cart')) {
        launch();
      } else {
        customElements.whenDefined('fly-to-cart').then(launch).catch(finish);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Destination helpers
  // ---------------------------------------------------------------------------

  function getCartDestinationPoint() {
    return (
      getVisibleCartIconPoint() ||
      getVisibleCartBubblePoint() ||
      getVisibleCartLinkPoint() ||
      getFallbackCartPoint()
    );
  }

  function getVisibleCartIconPoint() {
    const icons = Array.from(document.querySelectorAll('cart-icon'));

    const scored = icons
      .map((el) => {
        // FIX #5: Only check the element's own computed style, not ancestors.
        // Ancestor opacity checks caused valid cart icons to be rejected during
        // scroll-triggered header fades and page transitions.
        if (!isElementItselfVisible(el)) return null;

        const point = getPointFromViewportRect(el) || getPointFromRawRect(el);
        if (!point) return null;

        const inHeader = Boolean(el.closest('header, .shopify-section-header, sticky-header'));

        return {
          el,
          point,
          score: Math.hypot(window.innerWidth - point.x, point.y) + (inHeader ? 0 : 500),
        };
      })
      .filter(Boolean);

    if (!scored.length) return null;

    scored.sort((a, b) => a.score - b.score);

    return scored[0].point;
  }

  function getVisibleCartBubblePoint() {
    const bubbles = Array.from(
      document.querySelectorAll(
        'cart-icon [ref="cartBubble"], cart-icon [ref="cartBubbleText"], cart-icon [ref="cartBubbleCount"], [ref="cartBubble"], [ref="cartBubbleText"], [ref="cartBubbleCount"]'
      )
    );

    const scored = bubbles
      .map((el) => {
        if (!isElementItselfVisible(el)) return null;

        const point = getPointFromViewportRect(el) || getPointFromRawRect(el);
        if (!point) return null;

        const inHeader = Boolean(el.closest('header, .shopify-section-header, sticky-header'));

        return {
          el,
          point,
          score: Math.hypot(window.innerWidth - point.x, point.y) + (inHeader ? 0 : 500),
        };
      })
      .filter(Boolean);

    if (!scored.length) return null;

    scored.sort((a, b) => a.score - b.score);

    return scored[0].point;
  }

  function getVisibleCartLinkPoint() {
    const links = Array.from(
      document.querySelectorAll(
        'header a[href="/cart"], header a[href*="/cart"], .shopify-section-header a[href="/cart"], .shopify-section-header a[href*="/cart"]'
      )
    );

    const scored = links
      .map((el) => {
        if (!isElementItselfVisible(el)) return null;

        const point = getPointFromViewportRect(el) || getPointFromRawRect(el);
        if (!point) return null;

        return {
          el,
          point,
          score: Math.hypot(window.innerWidth - point.x, point.y),
        };
      })
      .filter(Boolean);

    if (!scored.length) return null;

    scored.sort((a, b) => a.score - b.score);

    return scored[0].point;
  }

  function getFallbackCartPoint() {
    const header = document.querySelector(
      'header, .shopify-section-header, .header, sticky-header'
    );

    const headerRect = header?.getBoundingClientRect?.();

    if (headerRect?.height) {
      return {
        x: window.innerWidth - 40,
        y: clamp(headerRect.top + headerRect.height / 2, 36, 92),
      };
    }

    return { x: window.innerWidth - 40, y: 44 };
  }

  // ---------------------------------------------------------------------------
  // Point helpers
  // ---------------------------------------------------------------------------

  /**
   * Reads the live viewport centre of an element, clamped to the *visible
   * intersection* of the element rect and the viewport (fix #4).
   *
   * @param {Element} el
   * @returns {{ x: number, y: number } | null}
   */
  function getLivePoint(el) {
    if (!el?.getBoundingClientRect) return null;

    const rect = el.getBoundingClientRect();

    if (!rect.width || !rect.height) return null;

    // Intersect element rect with viewport.
    const visLeft   = Math.max(rect.left,   0);
    const visTop    = Math.max(rect.top,    0);
    const visRight  = Math.min(rect.right,  window.innerWidth);
    const visBottom = Math.min(rect.bottom, window.innerHeight);

    if (visRight <= visLeft || visBottom <= visTop) {
      // Element is entirely off-screen — fall back to raw clamped centre so we
      // still get a reasonable starting point rather than bailing out.
      return {
        x: clamp(rect.left + rect.width  / 2, 24, window.innerWidth  - 24),
        y: clamp(rect.top  + rect.height / 2, 24, window.innerHeight - 24),
      };
    }

    // Centre of the visible slice.
    return {
      x: (visLeft + visRight)  / 2,
      y: (visTop  + visBottom) / 2,
    };
  }

  function getPointFromViewportRect(el) {
    const rect = getViewportRect(el);
    if (!rect) return null;

    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  function getPointFromRawRect(el) {
    if (!el?.getBoundingClientRect) return null;
    if (!isElementItselfVisible(el)) return null;

    const rect = el.getBoundingClientRect();

    if (!rect.width || !rect.height) return null;

    return {
      x: clamp(rect.left + rect.width / 2, 24, window.innerWidth - 24),
      y: clamp(rect.top + rect.height / 2, 24, window.innerHeight - 24),
    };
  }

  function getViewportRect(el) {
    if (!el?.getBoundingClientRect) return null;
    if (!isElementItselfVisible(el)) return null;

    const rect = el.getBoundingClientRect();

    if (!rect.width || !rect.height) return null;

    const left = clamp(rect.left, 0, window.innerWidth);
    const top = clamp(rect.top, 0, window.innerHeight);
    const right = clamp(rect.right, 0, window.innerWidth);
    const bottom = clamp(rect.bottom, 0, window.innerHeight);

    if (right <= left || bottom <= top) return null;

    return {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }

  /**
   * FIX #5: Checks only the element's *own* computed style for visibility,
   * not its ancestors.
   *
   * @param {Element} el
   * @returns {boolean}
   */
  function isElementItselfVisible(el) {
    if (!el) return false;

    if (el.hasAttribute('hidden')) return false;

    const style = window.getComputedStyle(el);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.visibility !== 'collapse' &&
      Number(style.opacity) !== 0
    );
  }

  /**
   * Legacy full-ancestor-walk visibility check. Kept for any internal callers
   * that genuinely need to know whether an element and all its ancestors are
   * visible (currently none — all destination lookups now use
   * isElementItselfVisible).
   *
   * @param {Element} el
   * @returns {boolean}
   */
  function isUsableElement(el) {
    if (!el) return false;

    let node = el;

    while (node && node.nodeType === 1) {
      const style = window.getComputedStyle(node);

      if (
        node.hasAttribute('hidden') ||
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        Number(style.opacity) === 0
      ) {
        return false;
      }

      node = node.parentElement;
    }

    return true;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * @param {Event} event
   * @param {string} selector
   * @returns {Element | null}
   */
  function closestFromEvent(event, selector) {
    const target = event.target;

    if (target instanceof Element) {
      return target.closest(selector);
    }

    if (target instanceof Node) {
      return target.parentElement?.closest(selector) ?? null;
    }

    return null;
  }

  /**
   * @param {Record<string, string | number>} body
   * @returns {RequestInit}
   */
  function cartFetchConfig(body) {
    return {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
      // Let the write finish even if the user navigates away mid-request (the
      // exact "add a few fast, then open a product page" case). Without this the
      // browser aborts the in-flight /cart/update on navigation, so the cart
      // lands on fewer items than were tapped. Body is tiny, well under the 64KB
      // keepalive limit.
      keepalive: true,
    };
  }

  function escapeCssUrl(url) {
    return String(url).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function toJsonRoute(route) {
    return String(route || '').endsWith('.js') ? route : `${route}.js`;
  }
})();
